import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, BrainCircuit, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { resetPassword } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("This reset link is missing its token. Request a new one.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const me = await resetPassword(token, password);
      setUser(me);
      toast.success("Password updated. You're signed in.");
      navigate("/history");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset your password. Request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-5 py-16 text-[var(--text)]">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#05060a]/40 via-transparent to-[#05060a]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md glass neon-border rounded-2xl p-8 scanlines"
        data-testid="reset-card"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-7 w-fit" data-testid="reset-logo">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-neon-cyan/50 glow-cyan">
            <BrainCircuit size={20} className="text-neon-cyan" strokeWidth={2} />
          </span>
          <span className="font-display font-bold text-lg tracking-[0.18em] text-white text-glow-cyan">
            Coretext
          </span>
        </Link>

        <p className="font-mono text-[10px] tracking-[0.3em] text-neon-amber mb-2">RESET PASSWORD</p>
        <h1 className="font-display text-3xl font-bold tracking-tight leading-none mb-2 text-white">
          Set a new password.
        </h1>
        <p className="font-mono text-sm text-[var(--muted)] mb-6 leading-relaxed">
          Choose a strong password you'll remember. You'll be signed in right after.
        </p>

        {!token ? (
          <div className="glass rounded-xl p-4 font-mono text-sm text-neon-red border border-neon-red/40" data-testid="reset-no-token">
            This reset link is invalid or incomplete.{" "}
            <Link to="/login" className="text-neon-cyan hover:underline">Request a new one</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                data-testid="reset-password-input"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 8 chars)"
                className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-11 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
              />
              <button
                type="button"
                data-testid="reset-toggle-visibility"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[var(--muted)] hover:text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="relative">
              <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                data-testid="reset-confirm-input"
                type={showPassword ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
              />
            </div>
            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading}
              style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
              className="w-full rounded-lg font-display font-bold tracking-wide py-3 flex items-center justify-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200 disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> UPDATING...</>
              ) : (
                <>UPDATE PASSWORD <ArrowRight size={18} strokeWidth={2.5} /></>
              )}
            </button>
          </form>
        )}

        <p className="font-mono text-xs text-[var(--muted)] mt-5 text-center">
          <Link to="/login" data-testid="reset-back-to-login" className="text-neon-cyan hover:underline">
            ← Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
